import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { AnimatePresence } from 'framer-motion';
import { FiCheck, FiX } from 'react-icons/fi';
import { mapValues, memoize } from 'lodash';

import {
    __t,
    defaultDynamicFieldLengthLimit,
    mapDynamicFieldToName,
    mapDynamicInputsToValidationSchema,
} from '@mssgme/shared';
import { FormikProvider, LandingsPropTypes, SubmitButton, useEnhancedFormik, getDefaultCountriesOptions } from '@mssgme/ui';
import { DynamicFormInputs, ThemedButton, ThemedModal } from '../../../UI';

import styles from './SubscriptionFormContent.scss';

const defaultCountry = memoize(() => getDefaultCountriesOptions('en')[0].value);

const getFormDefaults = (fields) => Object.fromEntries(
    fields.map((field, index) => {
        let value;

        switch (field.type) {
            case 'country':
                value = defaultCountry();
                break;
            case 'checkbox':
                value = [];
                break;
            default:
                value = '';
        }

        return [mapDynamicFieldToName(field, index), value];
    })
);

export const SubscriptionFormContent = ({ block, theme, onSubmit: rawSubmit, interactive, ...rest }) => {
    const [sent, setSent] = useState(null);
    const validationSchema = useMemo(() => mapDynamicInputsToValidationSchema(block), [block]);
    const FormTag = interactive ? 'form' : 'div';

    const [{ loading, error }, onSubmit] = useAsyncFn(
        async (values) => {
            if (false === (await rawSubmit(values))) {
                return;
            }

            const items = block.items.map((item, index) => {
                const field = mapDynamicFieldToName(item, index);
                const value = values[item._id] ?? values[field];

                return {
                    key: field,
                    label: item.label,
                    value,
                };
            });
            const pairs = items.filter(({ value }) => value);

            setSent(pairs);
        },
        [rawSubmit]
    );

    const formik = useEnhancedFormik({
        initialValues: useMemo(() => getFormDefaults(block.items), [block.items]),
        validationSchema,
        onSubmit,
    });

    const handleSentModalClosed = useCallback(() => {
        setSent(false);
        formik.resetForm();
    }, [formik]);

    useEffect(() => {
        if (sent && block.hasUrl && block.sentUrl) {
            const timer = setTimeout(() => (window.location = block.sentUrl), 1000);

            return () => clearTimeout(timer);
        }
    }, [sent, block.hasUrl, block.sentUrl, formik]);

    useEffect(() => {
        formik.setErrors(mapValues(error?.errors, (value) => __t(value)));
    }, [error]);

    return (
        <FormikProvider value={formik}>
            <FormTag id={`form_${block._id}`} {...rest}>
                <DynamicFormInputs theme={theme} block={block} defaultMaxSize={defaultDynamicFieldLengthLimit} />

                <div className={styles.buttonContainer}>
                    <SubmitButton
                        as={ThemedButton}
                        theme={theme}
                        isInitialHidden={false}
                        fullWidth
                        isFetching={loading}
                        disabled={!block.items.length}
                        style={{
                            backgroundColor: theme.buttonBackground,
                            color: theme.buttonTextColor,
                            borderRadius: theme.borderRadius,
                        }}
                    >
                        {block.sendLabel}
                    </SubmitButton>
                </div>
            </FormTag>

            <AnimatePresence>
                {sent && (
                    <ThemedModal compact overlay showHeader={false} onClose={handleSentModalClosed}>
                        {(handleClose) => (
                            <div className={styles.successContent}>
                                <div className={styles.closeIcon} onClick={handleClose}>
                                    <FiX size={26} />
                                </div>
                                <FiCheck size={60} className={styles.icon} />
                                <p className={styles.text}>{block.sentText}</p>
                            </div>
                        )}
                    </ThemedModal>
                )}
            </AnimatePresence>
        </FormikProvider>
    );
};

SubscriptionFormContent.propTypes = {
    block: LandingsPropTypes.block.isRequired,
    theme: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    interactive: PropTypes.bool,
};
